export type PortalRoleId = 'super-admin' | 'admin' | 'user'

export type PermissionLevel = 'full' | 'limited' | 'read-only' | 'none'

export interface PortalRoleDefinition {
  id: PortalRoleId
  label: string
  audience: string
  oktaGroup: string
  summary: string
}

export interface RolePermissionDefinition {
  id: string
  area: string
  category: 'Customer' | 'Loyalty' | 'Support' | 'Operations' | 'Administration'
  access: Record<PortalRoleId, {
    level: PermissionLevel
    detail: string
  }>
}

export const PORTAL_ROLES: readonly PortalRoleDefinition[] = [
  {
    id: 'super-admin',
    label: 'Super Admin',
    audience: 'RGM, Ops Director, and above',
    oktaGroup: "Barry's-Super-Admin",
    summary: 'Full customer, loyalty, merge, audit, and access-management tools.',
  },
  {
    id: 'admin',
    label: 'Admin',
    audience: 'AGM, GM, and SGM',
    oktaGroup: "Barry's-Admin-Portal-Admins",
    summary: 'Daily customer-support tools with high-risk actions restricted.',
  },
  {
    id: 'user',
    label: 'User',
    audience: 'COS and COL',
    oktaGroup: "Barry's-Admin-Portal-Users",
    summary: 'Customer history, profile notes and tags, gift-card resends, and individual challenge enrollment.',
  },
] as const

export const ROLE_PERMISSIONS: readonly RolePermissionDefinition[] = [
  {
    "id": "customer-profile",
    "area": "User Profile",
    "category": "Customer",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "Profile and class count, manage tags, see/add notes, memberships/packages/credits and attributes; linked-profile management and merged backups."
      },
      "admin": {
        "level": "limited",
        "detail": "Profile and class count, manage tags, see/add notes, memberships/packages/credits and attributes. No linked-profile management or merged backups."
      },
      "user": {
        "level": "limited",
        "detail": "Profile and class count, manage tags, see/add notes, memberships/packages/credits and attributes. No linked-profile management or merged backups."
      }
    }
  },
  {
    "id": "academy",
    "area": "The Academy",
    "category": "Loyalty",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "See/add notes, quarterly Star history, tier, Stars and point log; add/subtract Stars, move tiers and sync Mariana Tek."
      },
      "admin": {
        "level": "limited",
        "detail": "See/add notes, quarterly Star history, tier, Stars and point log; add/subtract Stars. No tier moves or sync."
      },
      "user": {
        "level": "limited",
        "detail": "See/add notes, quarterly Star history, tier, Stars and point log."
      }
    }
  },
  {
    "id": "offers",
    "area": "Current Offers",
    "category": "Loyalty",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "Read rewards, gift cards and discount codes; see/add notes; resend and issue rewards/cards; remove/sync rewards and deactivate cards. Existing code masks and amount limits apply by role."
      },
      "admin": {
        "level": "limited",
        "detail": "Read rewards, gift cards and discount codes; see/add notes; resend and issue approved rewards/cards. No removal, sync or deactivation. Codes masked."
      },
      "user": {
        "level": "limited",
        "detail": "Read rewards, gift cards and discount codes; see/add notes; resend an owned Shopify card to its stored recipient. No issuance, removal, sync or deactivation. Codes masked."
      }
    }
  },
  {
    "id": "purchases",
    "area": "Purchases",
    "category": "Customer",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "View purchases and deposit missing eligible purchase Stars."
      },
      "admin": {
        "level": "full",
        "detail": "View purchases and deposit missing eligible purchase Stars."
      },
      "user": {
        "level": "read-only",
        "detail": "View purchase history."
      }
    }
  },
  {
    "id": "reservations",
    "area": "Reservations",
    "category": "Customer",
    "access": {
      "super-admin": {
        "level": "read-only",
        "detail": "View reservation history."
      },
      "admin": {
        "level": "read-only",
        "detail": "View reservation history."
      },
      "user": {
        "level": "read-only",
        "detail": "View reservation history."
      }
    }
  },
  {
    "id": "challenge-data",
    "area": "Challenge Data",
    "category": "Operations",
    "access": {
      "super-admin": {
        "level": "limited",
        "detail": "View challenge sign-up, completion and prize status; enroll individual customers, adjust and issue prizes, and submit challenge help tickets. Completion bypass and destructive recovery retain separate safeguards."
      },
      "admin": {
        "level": "limited",
        "detail": "View challenge sign-up, completion and prize status; enroll individual customers, adjust and issue prizes, and submit challenge help tickets. Completion bypass and destructive recovery retain separate safeguards."
      },
      "user": {
        "level": "limited",
        "detail": "View sign-up, progress and prizes and enroll individual customers. No prize changes or help-ticket creation."
      }
    }
  },
  {
    "id": "email-comms",
    "area": "E-mail Comms",
    "category": "Support",
    "access": {
      "super-admin": {
        "level": "read-only",
        "detail": "View customer communication activity."
      },
      "admin": {
        "level": "read-only",
        "detail": "View customer communication activity."
      },
      "user": {
        "level": "read-only",
        "detail": "View customer communication activity."
      }
    }
  },
  {
    "id": "fwb",
    "area": "FWB",
    "category": "Support",
    "access": {
      "super-admin": {
        "level": "read-only",
        "detail": "View referral information."
      },
      "admin": {
        "level": "read-only",
        "detail": "View referral information."
      },
      "user": {
        "level": "read-only",
        "detail": "View referral information."
      }
    }
  },
  {
    "id": "help-tickets",
    "area": "Help Tickets",
    "category": "Support",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "View open customer tickets and create tickets."
      },
      "admin": {
        "level": "full",
        "detail": "View open customer tickets and create tickets."
      },
      "user": {
        "level": "read-only",
        "detail": "View open customer tickets. No ticket creation."
      }
    }
  },
  {
    "id": "fsa-receipts",
    "area": "FSA/HSA Receipts",
    "category": "Support",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "View and generate receipts."
      },
      "admin": {
        "level": "full",
        "detail": "View and generate receipts."
      },
      "user": {
        "level": "none",
        "detail": "Hidden; no access."
      }
    }
  },
  {
    "id": "bulk-points",
    "area": "Bulk Points",
    "category": "Administration",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "Bulk Star tools."
      },
      "admin": {
        "level": "none",
        "detail": "No access."
      },
      "user": {
        "level": "none",
        "detail": "No access."
      }
    }
  },
  {
    "id": "sessionm-tools",
    "area": "SM / ID Transfer",
    "category": "Administration",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "SessionM tools and platform ID transfer."
      },
      "admin": {
        "level": "none",
        "detail": "No access."
      },
      "user": {
        "level": "none",
        "detail": "No access."
      }
    }
  },
  {
    "id": "customer-merges",
    "area": "SM Merge",
    "category": "Administration",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "SessionM customer merge."
      },
      "admin": {
        "level": "none",
        "detail": "No access."
      },
      "user": {
        "level": "none",
        "detail": "No access."
      }
    }
  },
  {
    "id": "mt-merge",
    "area": "MT Merge",
    "category": "Administration",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "Mariana Tek merge with existing release and backup gates."
      },
      "admin": {
        "level": "none",
        "detail": "No access."
      },
      "user": {
        "level": "none",
        "detail": "No access."
      }
    }
  },
  {
    "id": "audit-logs",
    "area": "Audit Log",
    "category": "Administration",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "Application and gift-card audit history."
      },
      "admin": {
        "level": "none",
        "detail": "No access."
      },
      "user": {
        "level": "none",
        "detail": "No access."
      }
    }
  },
  {
    "id": "campaign-tools",
    "area": "Additional campaign tools",
    "category": "Operations",
    "access": {
      "super-admin": {
        "level": "limited",
        "detail": "Existing campaign and named-operator override rules apply."
      },
      "admin": {
        "level": "limited",
        "detail": "Existing campaign tools; no completion overrides."
      },
      "user": {
        "level": "none",
        "detail": "No campaign administration; customer enrollment is available in Challenge Data."
      }
    }
  },
  {
    "id": "user-management",
    "area": "User access management",
    "category": "Administration",
    "access": {
      "super-admin": {
        "level": "full",
        "detail": "Existing role-group management controls."
      },
      "admin": {
        "level": "none",
        "detail": "No access."
      },
      "user": {
        "level": "none",
        "detail": "No access."
      }
    }
  },
] as const

const ROLE_PRIORITY: readonly PortalRoleId[] = ['super-admin', 'admin', 'user']

export function portalRoleForGroups(groups: readonly { name: string }[] | readonly string[]): PortalRoleDefinition | null {
  const names = groups.map((group) => typeof group === 'string' ? group : group.name)
  const roleId = ROLE_PRIORITY.find((id) => {
    const role = PORTAL_ROLES.find((candidate) => candidate.id === id)
    return role ? names.includes(role.oktaGroup) : false
  })

  return roleId ? PORTAL_ROLES.find((role) => role.id === roleId) || null : null
}
